
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { KanbanConfig, KanbanColumn } from '../core/list-layout.types';
import draggable from 'vuedraggable';
import { getNestedProperty } from '../core/list-layout.utils';

interface KanbanColumnWithItems extends KanbanColumn {
    items: any[];
}

@Component({
    components: {
        draggable,
    },
})
export default class ListLayoutKanban extends Vue {
    @Prop({ required: true }) config!: KanbanConfig;
    @Prop({ type: Array, default: () => [] }) items!: any[];
    @Prop({ type: Boolean, default: false }) loading!: boolean;

    columnsWithItems: KanbanColumnWithItems[] = [];
    collapsedColumns: Set<string> = new Set();

    get cardStyle(): Record<string, string> {
        const style: Record<string, string> = {};
        if (this.config.cardHeight) {
            style.minHeight = `${this.config.cardHeight}px`;
        }
        return style;
    }

    mounted() {
        this.transformColumns();
        this.transformItems();
    }

    @Watch('config.columns', { deep: true })
    onColumnsChange() {
        this.transformColumns();
        this.transformItems();
    }

    @Watch('items', { deep: true })
    onItemsChange() {
        this.transformItems();
    }

    transformColumns() {
        this.columnsWithItems = this.config.columns.map((column) => ({
            ...column,
            items: [],
        }));
    }

    transformItems() {
        this.columnsWithItems.forEach((column) => {
            column.items = this.items.filter((item) => {
                const value = getNestedProperty(item, this.config.groupByProperty);
                return value === column.propertyValue;
            });
        });
    }

    getItemKey(item: any): string {
        return item.id || item.uid || `item-${Math.random()}`;
    }

    isColumnCollapsed(column: KanbanColumn): boolean {
        return this.collapsedColumns.has(column.propertyValue.toString());
    }

    toggleColumnCollapse(column: KanbanColumn) {
        const key = column.propertyValue.toString();
        if (this.collapsedColumns.has(key)) {
            this.collapsedColumns.delete(key);
        } else {
            this.collapsedColumns.add(key);
        }
        this.$forceUpdate();
    }

    handleCardClick(item: any) {
        this.$emit('item-click', item);
    }

    handleCardMove(event: any, column: KanbanColumn) {
        if (event.added) {
            const item = event.added.element;
            const oldValue = getNestedProperty(item, this.config.groupByProperty);

            // Update the item's group property
            this.setNestedProperty(item, this.config.groupByProperty, column.propertyValue);

            this.$emit('item-move', {
                item,
                from: oldValue,
                to: column.propertyValue,
                property: this.config.groupByProperty,
            });
        }
    }

    handleScroll(event: Event, column: KanbanColumn) {
        const target = event.target as HTMLElement;
        const scrollHeight = target.scrollHeight;
        const scrollTop = target.scrollTop;
        const clientHeight = target.clientHeight;

        // Check if near bottom (within 100px)
        if (scrollHeight - scrollTop - clientHeight < 100) {
            this.$emit('load-more', column);
        }
    }

    setNestedProperty(obj: any, path: string, value: any) {
        const keys = path.split('.');
        let current = obj;

        for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];
            if (!current[key]) {
                current[key] = {};
            }
            current = current[key];
        }

        current[keys[keys.length - 1]] = value;
    }
}
